import Head from 'expo-router/head';
import {
  DEFAULT_DESCRIPTION,
  DEFAULT_OG_IMAGE,
  OG_IMAGE_ALT,
  OG_IMAGE_HEIGHT,
  OG_IMAGE_TYPE,
  OG_IMAGE_WIDTH,
  SITE_NAME,
  absoluteUrl,
  pageTitle,
} from '../lib/seo';
import type { SeoProps } from './Seo.types';

export type { SeoProps };

/**
 * Per-route document head (web). Thin wrapper over `expo-router/head` that
 * fills in the full Open Graph + Twitter Card set from a couple of props so
 * call sites stay terse. Rendered tags are deduped by the head manager, so a
 * route's `<Seo>` overrides the defaults set in the root layout. Native gets
 * the no-op in `Seo.tsx`.
 */
export function Seo({
  title,
  description = DEFAULT_DESCRIPTION,
  path,
  image = DEFAULT_OG_IMAGE,
  type = 'website',
  noindex = false,
}: SeoProps) {
  const fullTitle = pageTitle(title);
  const imageUrl = absoluteUrl(image);
  const url = path ? absoluteUrl(path) : undefined;

  return (
    <Head>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {noindex ? <meta name="robots" content="noindex, nofollow" /> : null}
      {url ? <link rel="canonical" href={url} /> : null}

      {/* Open Graph */}
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={imageUrl} />
      {/* https-only origin, so secure_url mirrors og:image — Facebook/WhatsApp
          prefer it. Explicit dimensions + type let the crawler render the
          thumbnail on the first share without waiting on the image fetch. */}
      <meta property="og:image:secure_url" content={imageUrl} />
      <meta property="og:image:type" content={OG_IMAGE_TYPE} />
      <meta property="og:image:width" content={String(OG_IMAGE_WIDTH)} />
      <meta property="og:image:height" content={String(OG_IMAGE_HEIGHT)} />
      <meta property="og:image:alt" content={OG_IMAGE_ALT} />
      {url ? <meta property="og:url" content={url} /> : null}

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={imageUrl} />
      <meta name="twitter:image:alt" content={OG_IMAGE_ALT} />
    </Head>
  );
}
